/**
 * ProdutosTab - Tab for managing cliente products
 *
 * Server-side pagination, sorting, and search via MantineReactTable.
 */
import React, { useMemo, useState } from 'react';
import { Plus, Upload, FileDown, Package, Edit, Trash2, CheckCircle, Info, Search, RotateCcw } from 'lucide-react';
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
  type MRT_SortingState,
  type MRT_PaginationState,
} from 'mantine-react-table';
import { ActionIcon, Group, Badge, Switch } from '@mantine/core';
import type { ProductoCliente } from '../../types/produtoCliente';
import { MRT_LOCALIZATION_PT_BR } from '../hospital/tableLocalization';
import { ProdutoInfoModal } from './ProdutoInfoModal';
import { GTINInfoModal } from './GTINInfoModal';
import { ConsultarGTINsModal } from './ConsultarGTINsModal';
// N74 (Alana): reusa o modal de importação que já tem o modo "colar do Excel".
import { ImportModal } from '../produtos-cliente/ImportModal';
import PermissionGate from '../PermissionGate';

interface ProdutosTabProps {
  produtos: ProductoCliente[];
  loading: boolean;
  activeCount: number;
  totalRowCount: number;
  pagination: MRT_PaginationState;
  setPagination: React.Dispatch<React.SetStateAction<MRT_PaginationState>>;
  globalSearch: string;
  setGlobalSearch: (search: string) => void;
  sorting: MRT_SortingState;
  setSorting: React.Dispatch<React.SetStateAction<MRT_SortingState>>;
  onNewProduto: () => void;
  onEditProduto: (produto: ProductoCliente) => void;
  onDeleteProduto: (produto: ProductoCliente) => void;
  onRestoreProduto: (produto: ProductoCliente) => void;
  showDeleted: boolean;
  onToggleShowDeleted: (value: boolean) => void;
  onManageGTINs: (produto: ProductoCliente) => void;
  // N74: o ImportModal precisa do resultado para montar o resumo da importação.
  onImportProdutos: (file: File) => Promise<any>;
  onImportGTINs: (file: File) => Promise<any>;
  onExportExcel: () => void;
  exportingProdutos?: boolean;
  /** ID do cliente atual — necessario para a consulta de GTINs */
  clienteId?: number | null;
  /** Nome do cliente exibido no header do modal de consulta */
  clienteNome?: string;
}

export const ProdutosTab: React.FC<ProdutosTabProps> = ({
  produtos,
  loading,
  activeCount,
  totalRowCount,
  pagination,
  setPagination,
  globalSearch,
  setGlobalSearch,
  sorting,
  setSorting,
  onNewProduto,
  onEditProduto,
  onDeleteProduto,
  onRestoreProduto,
  showDeleted,
  onToggleShowDeleted,
  onManageGTINs,
  onImportProdutos,
  onImportGTINs,
  onExportExcel,
  exportingProdutos,
  clienteId,
  clienteNome,
}) => {
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showGTINInfoModal, setShowGTINInfoModal] = useState(false);
  // N74: qual catálogo o modal de importação está abrindo (null = fechado).
  const [importTab, setImportTab] = useState<'produtos' | 'gtin' | null>(null);
  const [showConsultarGTINsModal, setShowConsultarGTINsModal] = useState(false);

  // Columns definition — 3 separate columns for Descrição, Fabricante, Grupo
  const columns = useMemo<MRT_ColumnDef<ProductoCliente>[]>(
    () => [
      {
        accessorKey: 'codigo',
        header: 'Código',
        size: 120,
        Cell: ({ cell }) => (
          <span className="font-mono text-xs font-medium text-gray-900 dark:text-gray-300 block truncate">
            {cell.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: 'descricao_completa',
        header: 'Descrição',
        size: 250,
        Cell: ({ cell }) => (
          <span className="text-sm text-gray-900 dark:text-gray-300 line-clamp-2" title={cell.getValue<string>()}>
            {cell.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: 'fabricante',
        header: 'Fabricante',
        size: 150,
        Cell: ({ cell }) => (
          <span className="text-xs text-gray-700 dark:text-gray-400 truncate" title={cell.getValue<string>() || ''}>
            {cell.getValue<string>() || '-'}
          </span>
        ),
      },
      {
        accessorKey: 'grupo',
        header: 'Grupo',
        size: 120,
        Cell: ({ cell }) => {
          const val = cell.getValue<string>();
          return val ? (
            <Badge size="xs" variant="light" color="blue">{val}</Badge>
          ) : <span className="text-xs text-gray-400">-</span>;
        },
      },
      {
        accessorKey: 'unidade_medida',
        header: 'UM',
        size: 60,
      },
      {
        accessorKey: 'rms',
        header: 'RMS',
        size: 120,
        accessorFn: (row: any) => row.rms || '',
        Cell: ({ row }) => {
          const rms = (row.original as any).rms;
          return rms && rms.trim() ? (
            <div className="space-y-0.5">
              <span className="text-xs text-gray-700 dark:text-gray-400 font-mono block truncate" title={rms}>
                {rms}
              </span>
              <span className="text-[0.65rem] text-green-600 dark:text-green-500 flex items-center gap-0.5">
                <CheckCircle className="w-3 h-3" />
                Válido
              </span>
            </div>
          ) : (
            <span className="text-xs text-gray-400 dark:text-gray-600">-</span>
          );
        }
      },
      {
        accessorKey: 'produto_ativo',
        header: 'Status',
        size: 80,
        Cell: ({ cell }) => (
          showDeleted ? (
            <Badge color="gray" variant="filled" size="xs">Excluído</Badge>
          ) : (
            <Badge color={cell.getValue<boolean>() ? 'green' : 'red'} variant="light" size="xs">
              {cell.getValue<boolean>() ? 'Ativo' : 'Inativo'}
            </Badge>
          )
        ),
      },
    ],
    [showDeleted]
  );

  const table = useMantineReactTable({
    columns,
    data: produtos,

    // SERVER-SIDE MODE
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    rowCount: totalRowCount,

    // State bindings
    state: {
      pagination,
      sorting,
      globalFilter: globalSearch,
      isLoading: loading,
      showColumnFilters: false,
      density: 'xs',
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalSearch,

    // Features
    enableColumnFilters: false,
    enableGlobalFilter: true,
    enableSorting: true,
    enablePagination: true,
    enableRowActions: true,
    positionActionsColumn: 'last',
    enableDensityToggle: false,

    mantineTableProps: {
      highlightOnHover: true,
      striped: true,
      withTableBorder: true,
      withColumnBorders: true,
      sx: {
        'th': { padding: '8px 12px !important' },
        'td': { padding: '4px 12px !important' }
      }
    },
    renderRowActions: ({ row }) => (
      showDeleted ? (
        <Group gap={4} wrap="nowrap">
          <PermissionGate tela="PRODUTOS_LIST" acao="editar">
            <ActionIcon
              onClick={() => onRestoreProduto(row.original)}
              variant="subtle"
              color="green"
              size="sm"
              title="Restaurar"
            >
              <RotateCcw size={14} />
            </ActionIcon>
          </PermissionGate>
        </Group>
      ) : (
        <Group gap={4} wrap="nowrap">
          <ActionIcon
            onClick={() => onManageGTINs(row.original)}
            variant="subtle"
            color="blue"
            size="sm"
            title="GTINs"
          >
            <Package size={14} />
          </ActionIcon>
          <PermissionGate tela="PRODUTOS_LIST" acao="editar">
            <ActionIcon
              onClick={() => onEditProduto(row.original)}
              variant="subtle"
              color="gray"
              size="sm"
              title="Editar"
            >
              <Edit size={14} />
            </ActionIcon>
          </PermissionGate>
          <PermissionGate tela="PRODUTOS_LIST" acao="excluir">
            <ActionIcon
              onClick={() => onDeleteProduto(row.original)}
              variant="subtle"
              color="red"
              size="sm"
              title="Deletar"
            >
              <Trash2 size={14} />
            </ActionIcon>
          </PermissionGate>
        </Group>
      )
    ),
    mantinePaperProps: { shadow: 'none', withBorder: true, radius: 'md' },
    localization: MRT_LOCALIZATION_PT_BR,
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {showDeleted ? 'Produtos Excluídos' : 'Produtos do Cliente'}
        </h3>
        <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
          showDeleted
            ? 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
            : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
        }`}>
          {showDeleted ? `Excluídos: ${totalRowCount}` : `Produtos Ativos: ${activeCount}`}
        </div>
      </div>

      {/* Action Toolbar */}
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <Switch
            checked={showDeleted}
            onChange={(e) => onToggleShowDeleted(e.currentTarget.checked)}
            label="Mostrar excluídos"
            size="sm"
            color="red"
          />
          <div className="flex gap-2">
            <PermissionGate tela="PRODUTOS_LIST" acao="exportar">
              <button
                onClick={onExportExcel}
                disabled={exportingProdutos}
                className="flex items-center gap-2 px-4 py-2 h-9 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm disabled:opacity-50"
              >
                {exportingProdutos ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <FileDown className="w-4 h-4" />
                )}
                {exportingProdutos ? 'Exportando...' : 'Exportar Excel'}
              </button>
            </PermissionGate>

            <PermissionGate tela="PRODUTOS_LIST" acao="criar">
              <button
                onClick={onNewProduto}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Novo Produto
              </button>
            </PermissionGate>

            {/* N74 (Alana): era um <input type="file"> cru, só arquivo. Agora abre
                o mesmo ImportModal da tela de Produtos do Cliente, que já tem o
                modo "colar do Excel" que ela pediu. */}
            <PermissionGate tela="PRODUTOS_LIST" acao="criar">
              <button
                onClick={() => setImportTab('produtos')}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Upload className="w-4 h-4" />
                Importar Produtos
              </button>
            </PermissionGate>

            <PermissionGate tela="PRODUTOS_LIST" acao="criar">
              <button
                onClick={() => setShowInfoModal(true)}
                className="p-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-lg transition"
                title="Informações sobre importação"
              >
                <Info size={18} />
              </button>
            </PermissionGate>

            <PermissionGate tela="PRODUTOS_LIST" acao="criar">
              <button
                onClick={() => setImportTab('gtin')}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Upload className="w-4 h-4" />
                Importar GTINs
              </button>
            </PermissionGate>

            <PermissionGate tela="PRODUTOS_LIST" acao="criar">
              <button
                onClick={() => setShowGTINInfoModal(true)}
                className="p-2 bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/30 dark:hover:bg-purple-800/50 text-purple-600 dark:text-purple-300 rounded-lg transition"
                title="Informações sobre importação de GTINs"
              >
                <Info size={18} />
              </button>
            </PermissionGate>

            <button
              onClick={() => setShowConsultarGTINsModal(true)}
              disabled={!clienteId}
              className="p-2 bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/30 dark:hover:bg-purple-800/50 text-purple-600 dark:text-purple-300 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="Consultar GTINs cadastrados deste cliente"
            >
              <Search size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Table (always rendered — MRT handles loading/empty states internally) */}
      <MantineReactTable table={table} />

      {/* Info Modal - Produtos */}
      {showInfoModal && (
        <ProdutoInfoModal onClose={() => setShowInfoModal(false)} />
      )}

      {/* Info Modal - GTINs */}
      {showGTINInfoModal && (
        <GTINInfoModal onClose={() => setShowGTINInfoModal(false)} />
      )}

      {/* Consultar GTINs Modal */}
      <ConsultarGTINsModal
        show={showConsultarGTINsModal}
        clienteId={clienteId ?? null}
        clienteNome={clienteNome}
        onClose={() => setShowConsultarGTINsModal(false)}
      />

      {/* N74 (Alana): importação com "colar do Excel". A `key` remonta o modal ao
          alternar entre Produtos e GTIN, garantindo a aba certa e um formulário
          limpo (o modal guarda arquivo/texto colado em estado interno). */}
      {importTab && (
        <ImportModal
          key={importTab}
          isOpen
          initialTab={importTab}
          onClose={() => setImportTab(null)}
          onImportProducts={onImportProdutos}
          onImportGTINs={onImportGTINs}
        />
      )}
    </div>
  );
};
